import { useMemo } from 'react'

import { useCurrencySign } from 'app/common/components-next/currency'
import { Table, Td, Th, Tr } from 'app/common/ui-components/table'
import Sticky from 'app/common/templates-next/sticky'
import { renderCellGrayZeros } from 'app/common/templates-next/table-template'

import {
  columnKeysSimpleHeaders,
  createDataItemSimple,
  createDataProps,
  createHeaderCells,
  createHeaderCellsProps,
} from './table-options'

import { ByPaymentMethodSchool, ByPaymentMethodTotals } from './types'
import styles from './TableByPaymentMethod.module.scss'

type Props = {
  schools: Array<ByPaymentMethodSchool>
  totals: ByPaymentMethodTotals
}

export const TableByPaymentMethodSimple = ({ schools, totals }: Props) => {
  const currency = useCurrencySign()

  const headerCells = useMemo(() => {
    return createHeaderCells(currency)
  }, [currency])
  const cellProps = useMemo(() => {
    return createDataProps()
  }, [])

  const headerProps = useMemo(() => {
    return createHeaderCellsProps()
  }, [])

  const rows = useMemo(() => {
    return schools.reduce(
      (acc, { title, items }) => {
        return [
          ...acc,
          createDataItemSimple({ title, isOnlyTitle: true }),
          ...items.map(({ title: itemTitle, total: { count, sum } }) =>
            createDataItemSimple({ title: itemTitle, count, sum }),
          ),
        ]
      },
      [] as Array<ReturnType<typeof createDataItemSimple>>,
    )
  }, [schools])

  const totalRows = useMemo(() => {
    return [
      createDataItemSimple({
        title: 'Внутренний счет',
        count: totals.internal.total.count,
        sum: totals.internal.total.sum,
      }),
      createDataItemSimple({
        title: 'СБП',
        count: totals.sbp.total.count,
        sum: totals.sbp.total.sum,
      }),
      createDataItemSimple({
        title: 'Банковская карта',
        count: totals.bank_card.total.count,
        sum: totals.bank_card.total.sum,
      }),
      createDataItemSimple({
        title: 'Наличные',
        count: totals.cash.total.count,
        sum: totals.cash.total.sum,
      }),
      createDataItemSimple({
        title: 'ИТОГО',
        count: totals.grand_total.total.count,
        sum: totals.grand_total.total.sum,
      }),
    ]
  }, [totals])

  return (
    <Table wide borderBottom>
      <Sticky>
        {(ref, stuck, shadow) => (
          <thead ref={ref} className={styles.stickyFix}>
            {columnKeysSimpleHeaders.map((keys, index) => (
              <Tr header key={index} headerHasBorderBottom={false}>
                {keys.map((key, idx, arr) => (
                  <Th
                    scope="col"
                    key={key}
                    {...headerProps[key]}
                    borderTopCompensate={stuck}
                    bgFilled
                    borderBottom
                    borderRight={arr.length - 1 !== idx}
                  >
                    {headerCells[key]}
                  </Th>
                ))}
              </Tr>
            ))}
          </thead>
        )}
      </Sticky>
      <tbody>
        {rows.map(({ title, totalCount, totalSum, isOnlyTitle }, index) =>
          isOnlyTitle ? (
            <Tr key={index} interlineBorder>
              <Td {...cellProps.isOnlyTitle}>{title}</Td>
            </Tr>
          ) : (
            <Tr key={index} interlineBorder>
              <Td {...cellProps.title} style={{ paddingLeft: '50px' }} borderRight>
                {title}
              </Td>
              <Td {...cellProps.totalCount}>
                {renderCellGrayZeros({ columnKey: '', value: totalCount })}
              </Td>
              <Td {...cellProps.totalSum}>
                {renderCellGrayZeros({ columnKey: '', value: totalSum })}
              </Td>
            </Tr>
          ),
        )}
      </tbody>
      <Sticky stickyBottom>
        {(ref, stuck, shadow) => (
          <tfoot ref={ref}>
            {totalRows.map(({ title, totalCount, totalSum }, index) => (
              <Tr key={index}>
                <Td {...cellProps.title} borderTop={!index} bgColor="white" borderRight bold>
                  {title}
                </Td>
                <Td {...cellProps.totalCount} borderTop={!index} bold highlighted>
                  {renderCellGrayZeros({ columnKey: '', value: totalCount })}
                </Td>
                <Td {...cellProps.totalSum} borderTop={!index} bold highlighted>
                  {renderCellGrayZeros({ columnKey: '', value: totalSum })}
                </Td>
              </Tr>
            ))}
          </tfoot>
        )}
      </Sticky>
    </Table>
  )
}
